/*
 * Copyright © 2020-2024 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastModifyBy: pein
 * @LastModifyTime: 2024-12-17 14:36:34
 */

import React, { useEffect, useMemo, useState } from 'react'
import { Button, Layout } from 'antd'
import sensors from '../../utils/sensors'
import { exportUsers, searchDepartmentMember } from '../../actions/group'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import DownloadHistory from './DownloadHistory'
import MemberTable from './MemberTable'
import { DepartmentMember, DepartmentMemberV2 } from '../../global'
import { LeftOutlined } from '@ant-design/icons'

const { Content } = Layout

interface MemberSearchResultProps {
  onBatchDeleteBtnClick: (userList: DepartmentMember[]) => Promise<unknown>
  memberSearchValue: string
  onCancel: () => void
}

const MemberSearchResult = (props: MemberSearchResultProps) => {
  const { onBatchDeleteBtnClick, memberSearchValue, onCancel } = props
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((store) => store.group.groupId)
  const [downloadHistoryVisible, setDownloadHistoryVisible] = useState(false)
  const [users, setUsers] = useState<DepartmentMemberV2[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const [selectedRows, setSelectedRows] = useState<DepartmentMember[]>([])

  const rowSelection = {
    selectedRowKeys: selectedRowKeys,
    onChange: (selectedRowKeys: string[], selectedRows: DepartmentMember[]) => {
      setSelectedRowKeys(selectedRowKeys)
      setSelectedRows(selectedRows)
    }
  }

  const dataSource: DepartmentMember[] = useMemo(() => {
    return users
      .filter((user) => !user.quit)
      .map((user) => {
        return {
          departmentUserID: user.userID,
          userID: user.userID,
          nickname: user.name,
          headImgUrl: user.avatar,
          mobile: user.mobile || '',
          userStatus: user.quit ? -2 : 1,
          roleType: user.role,
          departmentNames: user.departmentNames || [],
          activeTime: user.activeTime || 0
        }
      })
  }, [users])

  const searchDepartmentUser = (searchName: string) => {
    dispatch(
      searchDepartmentMember({
        searchKey: searchName,
        scene: 'department_manage'
      })
    ).then((res) => {
      setUsers(res.member)
    })
  }

  useEffect(() => {
    if (memberSearchValue) {
      searchDepartmentUser(memberSearchValue)
    }
  }, [memberSearchValue])

  return (
    <Content className="content">
      <div className="member-table-container">
        <div className="title-wrap">
          <div
            className="title"
            style={{ color: '#444', cursor: 'pointer' }}
            onClick={onCancel}
          >
            <LeftOutlined style={{ color: '#444' }} />
            返回
          </div>
          <div className="actions">
            <Button
              type="primary"
              ghost
              onClick={() => {
                sensors.track('web_click_page_workgroup_groupcontacts', {
                  clickItem: 'exportMembers',
                  groupID: groupID
                })

                dispatch(exportUsers({ groupID })).then((res) => {
                  setDownloadHistoryVisible(true)
                })
              }}
            >
              导出名单
            </Button>

            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => {
                setDownloadHistoryVisible(true)
              }}
            >
              下载记录
            </Button>
          </div>
        </div>
        <div className="actions-bar">
          <div className="actions">
            <Button
              onClick={() => {
                sensors.track('web_click_page_workgroup_groupcontacts', {
                  clickItem: 'batchDeleteMember',
                  groupID: groupID
                })
                onBatchDeleteBtnClick(selectedRows)
                  .then(() => searchDepartmentUser(memberSearchValue))
                  .catch(() => {})
              }}
              style={{ color: '#ff4d4f', borderColor: '#ff4d4f' }}
            >
              批量删除
            </Button>
          </div>
        </div>
        <MemberTable
          dataSource={dataSource}
          rowSelection={rowSelection}
          refreshData={() => searchDepartmentUser(memberSearchValue)}
          handleDeleteClick={onBatchDeleteBtnClick}
        />
      </div>
      <DownloadHistory
        visible={downloadHistoryVisible}
        onClose={() => setDownloadHistoryVisible(false)}
      />
    </Content>
  )
}

export default MemberSearchResult
